import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import type { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { v4 } from "uuid";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useRequestsContext } from "../../contexts/RequestsContext";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { fetchTimeOffImpactForSave, timeOffImpactWindowKey, useInvalidatePtoImpact } from "../../hooks/useTimeOffImpact";
import { backendService } from "../../services/backend.service";
import { trackEvent } from "../../utils/analytics";
import { getStaffingDayStart, isAffectByDayStart } from "../../utils/dateUtils";
import {
	ChangesSource,
	type CreateSpecificPreferenceOptions,
	type DayOff,
	Preferences,
	type PtoTypes,
	type SpecificPreferencePayload,
	type TimeOffImpactShift,
} from "../../utils/models";

export const useAddDayOff = (nurseId: number, onSave: (dayOff: DayOff) => void, existingDayOff?: DayOff) => {
	const { chosenDepartment, removeNursesShiftsCompletely, addNewDBNurseShifts, afterScheduleWrite, showErrorSnackbar } = useHospitalData();
	const { showSnackbar } = useSnackbar();
	const { refreshRequests } = useRequestsContext();
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const invalidatePtoImpact = useInvalidatePtoImpact();
	const { trackError } = useAnalytics();
	const isEditMode = !!existingDayOff;

	const mutation = useMutation({
		mutationFn: async ({
			startDate,
			endDate,
			reason,
			hours,
			postShifts,
			notes,
			exact,
		}: {
			startDate: DateTime;
			endDate: DateTime;
			reason: PtoTypes;
			hours: number;
			postShifts: boolean;
			notes: string;
			// Exact-times (partial-day) PTO, DAT-230: wall-clock times within the single staffing
			// date startDate (=== endDate). The backend re-derives the window and hours from these.
			exact?: { startTime: DateTime; endTime: DateTime };
		}) => {
			const startInTz = startDate.setZone(chosenDepartment.timezone, { keepLocalTime: true });
			const endInTz = endDate.setZone(chosenDepartment.timezone, { keepLocalTime: true });

			let dateFrom: DateTime;
			let dateTo: DateTime;
			if (exact) {
				// Mirror the backend's resolution so the optimistic DayOff/preference carries the
				// real sub-day window (times before day_start belong to the staffing day's tail).
				dateFrom = isAffectByDayStart(startInTz, exact.startTime.hour, exact.startTime.minute, chosenDepartment.dayStart);
				dateTo = isAffectByDayStart(startInTz, exact.endTime.hour, exact.endTime.minute, chosenDepartment.dayStart);
				if (dateTo <= dateFrom) {
					dateTo = dateTo.plus({ days: 1 });
				}
			} else {
				dateFrom = getStaffingDayStart(startInTz, chosenDepartment.dayStart);
				dateTo = getStaffingDayStart(endInTz.plus({ days: 1 }), chosenDepartment.dayStart);
			}

			const dayOff: DayOff = {
				id: existingDayOff?.id ?? v4(),
				dateFrom,
				dateTo,
				hours,
				reasons: [reason],
				post_shifts: postShifts,
				notes,
			};
			// What the save will do to each overlapping shift, asked of the server BEFORE the POST —
			// afterwards the removed ones are gone and the answer no longer describes them. The window
			// is the EXACT committed one, never the preview's debounced value, which can still be
			// lagging behind it. The preview has usually already warmed this cache entry.
			//
			// Advisory, exactly like the preview: `fetchTimeOffImpactForSave` is un-retried and
			// deadline-bounded so failure and hang both land here in bounded time, and a full
			// department refresh then replaces the optimistic removal. The save always proceeds.
			let impactShifts: TimeOffImpactShift[] | null = null;
			if (!isEditMode && !exact) {
				const windowKey = timeOffImpactWindowKey({ start: dateFrom, end: dateTo });
				if (windowKey) {
					try {
						const impact = await fetchTimeOffImpactForSave(queryClient, chosenDepartment.department_id, nurseId, windowKey, trackError);
						impactShifts = impact.shifts;
					} catch (error) {
						trackError("time_off_impact_prefetch_error", {
							error_message: error instanceof Error ? error.message : String(error),
							nurse_id: nurseId,
							department_id: chosenDepartment.department_id,
						});
					}
				}
			}

			const startDateIso = startInTz.toISODate();
			const endDateIso = exact ? startDateIso : endInTz.toISODate();
			if (!startDateIso || !endDateIso) {
				throw new Error("Invalid start/end date for day off");
			}
			const payload: SpecificPreferencePayload = {
				id: dayOff.id,
				type: Preferences.Avoid,
				start_date: startDateIso,
				end_date: endDateIso,
				nurse_id: nurseId,
				reason,
				hours,
				notes,
				post_shifts: postShifts,
				start_time: exact?.startTime.toFormat("HH:mm"),
				end_time: exact?.endTime.toFormat("HH:mm"),
			};
			const body: CreateSpecificPreferenceOptions = { preference: payload, source: ChangesSource.Profile };

			if (isEditMode) {
				const result = await backendService.updateDayOffPreference(chosenDepartment.department_id, nurseId, dayOff.id, body);
				const { shiftsToAdd, shiftsToRemove } = result.data;

				removeNursesShiftsCompletely(shiftsToRemove);
				addNewDBNurseShifts(shiftsToAdd);
			} else {
				await backendService.addDayOffPreference(chosenDepartment.department_id, body);
				if (!exact) {
					if (impactShifts) {
						removeNursesShiftsCompletely(impactShifts.filter((shift) => shift.overlap === "remove").map((shift) => shift.id));
						if (impactShifts.some((shift) => shift.overlap !== "remove" && shift.overlap !== "none")) {
							// Trims and splits rewrite shifts server-side (new segment rows, changed times)
							// that the create response doesn't return, so refetch.
							await afterScheduleWrite({ allWeeks: true });
						}
					} else {
						// The impact lookup failed, so what the server just did to these shifts is unknown —
						// resync rather than leave removed shifts on screen until the next reload.
						await afterScheduleWrite({ allWeeks: true });
					}
				}
			}

			if (exact) {
				// Partial windows TRIM/SPLIT shifts server-side (new segment rows, changed times) —
				// state the FE can't mirror optimistically, so refetch instead.
				await afterScheduleWrite({ allWeeks: true });
			}

			onSave(dayOff);

			await refreshRequests();

			return dayOff;
		},
		onSuccess: async (_, data) => {
			trackEvent(isEditMode ? "day_off_updated" : "day_off_added", { ...data, nurseId });
			showSnackbar(isEditMode ? "Day off updated successfully" : "Day off added successfully", "success");
			await invalidatePtoImpact();
		},
		onError: (error) => {
			const axiosError = error as AxiosError<{ detail?: { message?: string; error_type?: string } | string }>;
			const errorDetail = axiosError?.response?.data?.detail;
			let errorMessage = "Error updating days off";

			if (errorDetail) {
				if (typeof errorDetail === "object" && errorDetail?.error_type === "UIVisibleError" && errorDetail?.message) {
					errorMessage = t(errorDetail.message);
				} else if (typeof errorDetail === "string") {
					try {
						const parsed = JSON.parse(errorDetail);
						if (parsed?.error_type === "UIVisibleError" && parsed?.message) {
							errorMessage = t(parsed.message);
						}
					} catch {
						if (errorDetail.includes("error.pto.intersects") || errorDetail.includes("intersect")) {
							errorMessage = t("error.pto.intersects");
						} else {
							errorMessage = errorDetail.length < 200 ? errorDetail : errorMessage;
						}
					}
				}
			}

			showErrorSnackbar(errorMessage, { action: "error updating days off", nurse_id: nurseId }, error);
		},
	});

	return mutation;
};
